"use client";

import * as React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { icons } from "../../assets/icons";
import {
  validarUnidadYGetRoles,
  type UnidadResidencial,
} from "../../api/authService";

export default function UnitSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, password, usuario, unidades } = location.state || {
    email: "",
    password: "",
    usuario: null,
    unidades: [], // Valor por defecto como array vacío
  };

  const [search, setSearch] = React.useState("");
  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Asegurar que unidades sea siempre un array antes de filtrar
  const safeUnidades = Array.isArray(unidades) ? unidades : [];
  
  // Filtrar unidades por búsqueda
  const filteredUnits = safeUnidades.filter((u: UnidadResidencial) =>
    u.nombre?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = async (unidadId: string, unidadNombre: string) => {
    try {
      setError(null);
      setLoadingId(unidadId);

      // Llamada a Autenticacion_2 → devuelve lista y mapeamos a roles
      const { roles } = await validarUnidadYGetRoles({
        email,
        contrasena: password,
        CodigoUnidadResidencial: unidadId,
      });

      if (!roles.length) {
        setError(
          "No encontramos roles para esta unidad. Contacta a la administración."
        );
        return;
      }

      // Pasar usuario al siguiente paso incluyendo las unidades y la contraseña
      navigate("/RoleSelectionPage", {
        state: {
          email,
          password, // IMPORTANTE: Pasar la contraseña al siguiente paso
          usuario,
          unidadId,
          unidadNombre,
          roles,
          unidades, // IMPORTANTE: Pasar las unidades para poder regresar
        },
      });
    } catch (e: any) {
      setError(e?.message || "No fue posible validar la unidad.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleGoBack = () => {
    navigate("/", { replace: true });
  };

  // Si no hay datos de navegación válidos, redirigir al login
  React.useEffect(() => {
    if (!email || !password || !Array.isArray(unidades) || unidades.length === 0) {
      console.log("Datos inválidos, redirigiendo al login:", { email, password, unidades });
      navigate("/", { replace: true });
    }
  }, [email, password, unidades, navigate]);

  // Mostrar loading si aún no hay datos
  if (!email || !password || !Array.isArray(unidades)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-lg shadow-xl border border-gray-200 rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardHeader>
          <div className="flex flex-col items-center text-center">
            <img src={icons.logo} alt="EasyPH Logo" className="w-60 h-auto" />
            <Button
              onClick={handleGoBack}
              className="mt-2 w-full text-white transition-all duration-200 shadow-md font-medium py-2 px-4 rounded-lg"
              style={{
                backgroundColor: "#DF8037",
                borderColor: "#DF8037",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#E55A00";
                e.currentTarget.style.borderColor = "#E55A00";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#DF8037";
                e.currentTarget.style.borderColor = "#DF8037";
              }}
            >
              Regresar al Login
            </Button>
            <CardTitle className="text-2xl font-bold text-military-600 mt-3">
              {email}
            </CardTitle>
            <p className="text-base font-medium text-gray-600 mt-2">
              Selecciona tu unidad residencial
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <Input
              placeholder="Buscar unidad..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white border border-gray-300 focus:border-military-500 focus:ring-military-500 rounded-lg"
            />
          </div>

          <div className="space-y-3">
            {filteredUnits.map((unit: UnidadResidencial) => (
              <Button
                key={unit.id}
                onClick={() => handleSelect(unit.id, unit.nombre)}
                disabled={loadingId === unit.id}
                className="w-full bg-military-500 hover:bg-military-600 text-white shadow-md rounded-lg disabled:opacity-50"
              >
                {loadingId === unit.id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validando...
                  </>
                ) : (
                  unit.nombre
                )}
              </Button>
            ))}
            {filteredUnits.length === 0 && safeUnidades.length > 0 && (
              <p className="text-center text-gray-500 text-sm">
                No se encontraron resultados
              </p>
            )}
            {safeUnidades.length === 0 && (
              <p className="text-center text-gray-500 text-sm">
                No hay unidades disponibles
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}